import { useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAccount, usePortfolios } from "@/hooks/useData";
import AccountHeader from "@/components/analytics/AccountHeader";
import OverviewDashboard from "@/components/analytics/OverviewDashboard";
import AdvancedStats from "@/components/analytics/AdvancedStats";
import AccountShareDialog from "@/components/AccountShareDialog";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

const VALID_RANGES = ["today", "week", "month", "year", "all"];

export default function AccountAnalyticsPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: account, isLoading, error } = useAccount(accountId);
  const { data: portfolios } = usePortfolios();
  const [syncing, setSyncing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const afterDelete = () => {
    queryClient.invalidateQueries({ queryKey: ["portfolio-accounts"] });
    queryClient.invalidateQueries({ queryKey: ["portfolios"] });
    queryClient.invalidateQueries({ queryKey: ["accounts-summary"] });
    navigate(account?.portfolio_id ? `/app/portfolio/${account.portfolio_id}` : "/app/accounts", { replace: true });
  };

  const range = VALID_RANGES.includes(params.get("range")) ? params.get("range") : "month";
  const rawSymbol = params.get("symbol") || "ALL";
  // Guard against stale/invalid deep-links: only honour a symbol this account actually trades.
  const symbol = (account?.symbols || []).includes(rawSymbol) ? rawSymbol : "ALL";

  const setRange = (v) => setParams((p) => { const n = new URLSearchParams(p); n.set("range", v); return n; }, { replace: true });
  const setSymbol = (v) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v && v !== "ALL") n.set("symbol", v); else n.delete("symbol");
    return n;
  }, { replace: true });

  const rename = async (name) => {
    try {
      await api.patch(`/accounts/${accountId}`, { name });
      toast.success("Account renamed");
      queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      queryClient.invalidateQueries({ queryKey: ["portfolio-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["accounts-summary"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't rename account");
      throw err;
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post(`/accounts/${accountId}/sync`);
      toast.success("Sync started. Fresh data in a moment.");
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["account", accountId] });
        queryClient.invalidateQueries({ queryKey: ["overview", accountId] });
        setSyncing(false);
      }, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start sync");
      setSyncing(false);
    }
  };

  const portfolio = account?.is_demo
    ? { portfolio_id: "demo", name: "Demo accounts" }
    : (portfolios || []).find((p) => p.portfolio_id === account?.portfolio_id);

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-8">
        <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground" data-testid="account-breadcrumb">
          <Link to="/app/accounts" className="hover:text-foreground">All portfolios</Link>
          {portfolio && (
            <>
              <ChevronRight className="h-3 w-3" />
              <Link to={`/app/portfolio/${portfolio.portfolio_id}`} className="hover:text-foreground">
                {portfolio.name}
              </Link>
            </>
          )}
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{account?.name || "Account"}</span>
        </div>

        <button onClick={() => navigate(-1)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          data-testid="account-back-button">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>

        <div className="mt-3 space-y-4">
          {isLoading && (
            <>
              <Skeleton className="h-40 rounded-2xl" />
              <div className="grid gap-4 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
              </div>
            </>
          )}

          {!isLoading && error && (
            <div className="rounded-2xl border border-dashed bg-surface-2/40 p-10 text-center" data-testid="account-not-found">
              <AlertTriangle className="mx-auto h-6 w-6 text-muted-foreground" />
              <p className="mt-2 font-display text-lg font-semibold">Account not found</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                This account may have been removed, or you don't have access to it.
              </p>
              <Button className="mt-4" onClick={() => navigate("/app/accounts")}>Back to portfolios</Button>
            </div>
          )}

          {!isLoading && account && (
            <>
              <AccountHeader account={account} onRename={rename} onSync={syncNow} syncing={syncing} onShare={() => setShareOpen(true)}
                onDelete={() => setDeleteOpen(true)} />
              {account.status === "pending" ? (
                <div className="rounded-2xl border border-dashed bg-surface-2/40 p-10 text-center">
                  <p className="font-display text-lg font-semibold">Awaiting first sync</p>
                  <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                    Analytics appear here as soon as this account finishes its first sync.
                  </p>
                </div>
              ) : (
                <>
                  <OverviewDashboard
                    accountId={accountId}
                    currency={account.currency}
                    range={range}
                    onRangeChange={setRange}
                    symbol={symbol}
                    onSymbolChange={setSymbol}
                    symbols={account.symbols || []}
                  />
                  <AdvancedStats
                    accountId={accountId}
                    range={range}
                    symbol={symbol}
                    currency={account.currency}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      <AccountShareDialog accountId={accountId} accountName={account?.name} open={shareOpen} onClose={() => setShareOpen(false)} />
      <DeleteAccountDialog account={account} open={deleteOpen} onClose={() => setDeleteOpen(false)} onDeleted={afterDelete} />
    </AppShell>
  );
}
